import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Typography, Box } from '@mui/material';
import {
    Security as Shield,
    TrendingUp, People, TouchApp, Visibility,
    Public, Percent, CalendarMonth, Assessment, Timeline,
    BarChart as BarChartIcon
} from '@mui/icons-material';
import {
    LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    ComposedChart
} from 'recharts';
import metricsService from '../../services/metricsService';

const COLORS = {
    primary: '#1976d2',
    secondary: '#dc004e',
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    purple: '#9c27b0',
    teal: '#009688',
    indigo: '#3f51b5',
    lightBlue: '#90caf9',
    lightGreen: '#a5d6a7'
};

const AnalyticsDashboard = () => {
    const [metrics, setMetrics] = useState([]);
    const [summary, setSummary] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [metricsData, summaryData, startDateData] = await Promise.all([
                metricsService.getMetrics(),
                metricsService.getMetricsSummary(),
                metricsService.getStartDate().catch(() => ({ start_date: '' }))
            ]);
            setMetrics([...metricsData].reverse());
            setSummary(summaryData);
            setStartDate(startDateData.start_date || '');
        } catch (err) {
            setError('Failed to load analytics data. ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatStartDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const formatMonth = (monthStr) => {
        const date = new Date(monthStr + 'T00:00:00');
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    const chartData = metrics.map(m => ({
        month: formatMonth(m.month),
        mau: m.mau,
        dau: m.dau,
        retention_30: m.retention_30_day,
        retention_90: m.retention_90_day,
        retention_120: m.retention_120_day,
        sessions: m.avg_sessions_per_user,
        duration: m.avg_session_duration,
        features: m.core_features_used,
        growth: m.growth_rate,
        clicks: m.clicks,
        impressions: m.impressions,
        ctr: m.impressions > 0 ? parseFloat(((m.clicks / m.impressions) * 100).toFixed(2)) : 0,
        uk: m.uk_users,
        nigeria: m.nigeria_users
    }));

    const latestMetric = metrics.length > 0 ? metrics[metrics.length - 1] : null;

    const geoData = latestMetric ? [
        { name: 'UK', value: latestMetric.uk_users },
        { name: 'Nigeria', value: latestMetric.nigeria_users }
    ] : [];
    const GEO_COLORS = [COLORS.primary, COLORS.success];

    const allTimeStats = metrics.length > 0 ? {
        totalClicks: metrics.reduce((sum, m) => sum + (m.clicks || 0), 0),
        totalImpressions: metrics.reduce((sum, m) => sum + (m.impressions || 0), 0),
        peakMAU: Math.max(...metrics.map(m => m.mau || 0)),
        peakDAU: Math.max(...metrics.map(m => m.dau || 0)),
        avgRetention30: parseFloat((metrics.reduce((sum, m) => sum + (m.retention_30_day || 0), 0) / metrics.length).toFixed(1)),
        avgRetention90: parseFloat((metrics.reduce((sum, m) => sum + (m.retention_90_day || 0), 0) / metrics.length).toFixed(1)),
        avgRetention120: parseFloat((metrics.reduce((sum, m) => sum + (m.retention_120_day || 0), 0) / metrics.length).toFixed(1)),
        avgGrowth: parseFloat((metrics.reduce((sum, m) => sum + (m.growth_rate || 0), 0) / metrics.length).toFixed(1)),
        totalUK: metrics.reduce((sum, m) => sum + (m.uk_users || 0), 0),
        totalNigeria: metrics.reduce((sum, m) => sum + (m.nigeria_users || 0), 0),
        totalMonths: metrics.length,
        overallCTR: (() => {
            const totalC = metrics.reduce((sum, m) => sum + (m.clicks || 0), 0);
            const totalI = metrics.reduce((sum, m) => sum + (m.impressions || 0), 0);
            return totalI > 0 ? parseFloat(((totalC / totalI) * 100).toFixed(2)) : 0;
        })()
    } : null;

    const overviewTitle = startDate ? 'Tracking Overview' : 'All-Time Overview';
    const overviewSubtitle = startDate
        ? `Since ${formatStartDate(startDate)}`
        : `(${allTimeStats?.totalMonths || 0} ${allTimeStats?.totalMonths === 1 ? 'month' : 'months'} tracked)`;
    const trackingContext = startDate
        ? `Since ${formatStartDate(startDate)}`
        : `${metrics.length} ${metrics.length === 1 ? 'month' : 'months'} tracked`;

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Paper elevation={3} sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
                    {payload.map((entry, index) => (
                        <Typography key={index} variant="body2" sx={{ color: entry.color }}>
                            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
                        </Typography>
                    ))}
                </Paper>
            );
        }
        return null;
    };

    const StatCard = ({ icon, value, label, color }) => (
        <div style={{
            background: 'white', borderRadius: '16px', padding: '24px 16px',
            textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
            border: '1px solid #f3f4f6', borderTop: `4px solid ${color}`, flex: '1 1 140px', minWidth: 0
        }}>
            <div style={{ marginBottom: '8px' }}>{icon}</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color, lineHeight: 1.2 }}>{value}</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{label}</div>
        </div>
    );

    const ChartSection = ({ icon, title, description, timeframe, children }) => (
        <div style={{
            background: 'white', borderRadius: '16px', padding: '28px', marginBottom: '24px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {icon}
                    <span style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginLeft: '8px' }}>{title}</span>
                </div>
                {timeframe && <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{timeframe}</span>}
            </div>
            {description && <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>{description}</div>}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>{children}</div>
        </div>
    );

    return (
        <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', minHeight: '100vh', background: '#f9fafb' }}>
            {/* Header */}
            <header style={{
                position: 'fixed', top: 0, width: '100%',
                background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
                borderBottom: '1px solid #e5e7eb', zIndex: 50,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '64px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => navigate('/')}>
                            <div style={{ background: '#2563eb', padding: '8px', borderRadius: '8px' }}>
                                <Shield style={{ fontSize: 24, color: 'white' }} />
                            </div>
                            <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>RiskAS</span>
                        </div>
                        <nav style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                            <button onClick={() => navigate('/')} style={{ fontWeight: '500', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>Home</button>
                            <button onClick={() => navigate('/reviews')} style={{ fontWeight: '500', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>Reviews</button>
                            <span style={{ fontWeight: '600', color: '#2563eb', fontSize: '16px' }}>Analytics</span>
                            <button onClick={() => navigate('/login')} style={{ border: '2px solid #2563eb', color: '#2563eb', background: 'white', padding: '8px 16px', borderRadius: '8px', fontWeight: '500', cursor: 'pointer', fontSize: '16px' }}>Login</button>
                            <button onClick={() => navigate('/register')} style={{ background: '#2563eb', color: 'white', padding: '10px 24px', borderRadius: '8px', fontWeight: '500', border: 'none', cursor: 'pointer', fontSize: '16px' }}>Register</button>
                        </nav>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #eff6ff 100%)', paddingTop: '100px', paddingBottom: '48px' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <span style={{ background: '#dbeafe', color: '#1e40af', padding: '6px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>
                            Live Performance Data
                        </span>
                    </div>
                    <h1 style={{ fontSize: '42px', fontWeight: '800', color: '#111827', margin: '0 0 12px 0', lineHeight: 1.2 }}>
                        Application <span style={{ color: '#2563eb' }}>Analytics</span>
                    </h1>
                    <p style={{ fontSize: '18px', color: '#6b7280', maxWidth: '600px', margin: '0 auto 8px auto' }}>
                        Performance metrics tracked over across UK and Nigeria
                    </p>
                    {startDate && (
                        <p style={{ fontSize: '14px', color: '#7c3aed', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <CalendarMonth style={{ fontSize: 16 }} /> Tracking since {formatStartDate(startDate)}
                        </p>
                    )}

                    {loading && (
                        <div style={{ padding: '60px', textAlign: 'center' }}>
                            <div style={{ width: '48px', height: '48px', border: '4px solid #e5e7eb', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px auto' }} />
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            <p style={{ color: '#6b7280' }}>Loading analytics data...</p>
                        </div>
                    )}

                    {error && (
                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '16px', maxWidth: '500px', margin: '24px auto', color: '#dc2626' }}>
                            {error}
                        </div>
                    )}

                    {!loading && !error && summary && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center', marginTop: '32px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
                            <StatCard icon={<People style={{ fontSize: 32, color: COLORS.primary }} />} value={(summary.mau || 0).toLocaleString()} label="Monthly Active Users" color={COLORS.primary} />
                            <StatCard icon={<People style={{ fontSize: 32, color: COLORS.secondary }} />} value={(summary.dau || 0).toLocaleString()} label="Daily Active Users" color={COLORS.secondary} />
                            <StatCard icon={<TrendingUp style={{ fontSize: 32, color: COLORS.success }} />} value={`${summary.growth_rate || 0}%`} label="Growth Rate (MoM)" color={COLORS.success} />
                            <StatCard icon={<Percent style={{ fontSize: 32, color: COLORS.warning }} />} value={`${summary.retention_30_day || 0}%`} label="30-Day Retention" color={COLORS.warning} />
                        </div>
                    )}
                </div>
            </section>

            {/* Charts */}
            {!loading && !error && metrics.length > 0 && (
                <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 16px 48px 16px' }}>
                    {/* Tracking/All-Time Overview */}
                    {allTimeStats && (
                        <div style={{
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf5 100%)',
                            borderRadius: '16px', padding: '28px', marginBottom: '24px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e5e7eb'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                                <Timeline style={{ color: '#7c3aed', marginRight: '8px' }} />
                                <span style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{overviewTitle}</span>
                                <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>{overviewSubtitle}</span>
                            </div>
                            <div style={{ borderTop: '1px solid #d1d5db', paddingTop: '16px' }}>
                                {[
                                    [
                                        { icon: <TouchApp style={{ fontSize: 24, color: COLORS.primary }} />, val: allTimeStats.totalClicks.toLocaleString(), label: 'Total Clicks', c: COLORS.primary },
                                        { icon: <Visibility style={{ fontSize: 24, color: COLORS.indigo }} />, val: allTimeStats.totalImpressions.toLocaleString(), label: 'Total Impressions', c: COLORS.indigo },
                                        { icon: <Assessment style={{ fontSize: 24, color: COLORS.teal }} />, val: `${allTimeStats.overallCTR}%`, label: 'Overall CTR', c: COLORS.teal },
                                        { icon: <People style={{ fontSize: 24, color: COLORS.secondary }} />, val: allTimeStats.peakMAU.toLocaleString(), label: 'Peak MAU', c: COLORS.secondary },
                                    ],
                                    [
                                        { icon: <Percent style={{ fontSize: 24, color: COLORS.success }} />, val: `${allTimeStats.avgRetention30}%`, label: 'Avg 30-Day Retention', c: COLORS.success },
                                        { icon: <TrendingUp style={{ fontSize: 24, color: COLORS.warning }} />, val: `${allTimeStats.avgGrowth}%`, label: 'Avg Growth Rate', c: COLORS.warning },
                                        { icon: <Public style={{ fontSize: 24, color: COLORS.primary }} />, val: allTimeStats.totalUK.toLocaleString(), label: 'Total UK Users', c: COLORS.primary },
                                        { icon: <Public style={{ fontSize: 24, color: COLORS.success }} />, val: allTimeStats.totalNigeria.toLocaleString(), label: 'Total Nigeria Users', c: COLORS.success },
                                    ]
                                ].map((row, ri) => (
                                    <div key={ri} style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: ri === 0 ? '16px' : 0 }}>
                                        {row.map((s, i) => (
                                            <div key={i} style={{ flex: '1 1 22%', minWidth: '120px', textAlign: 'center', padding: '8px' }}>
                                                {s.icon}
                                                <div style={{ fontSize: '22px', fontWeight: '700', color: s.c }}>{s.val}</div>
                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>{s.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <ChartSection icon={<People style={{ fontSize: 22, color: COLORS.primary }} />} title="Monthly & Daily Active Users" timeframe={trackingContext}>
                        <Box sx={{ height: { xs: 280, sm: 350 }, width: '100%' }}>
                            <ResponsiveContainer>
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Line type="monotone" dataKey="mau" name="MAU" stroke={COLORS.primary} strokeWidth={3} dot={{ r: 5, fill: COLORS.primary }} activeDot={{ r: 7 }} />
                                    <Line type="monotone" dataKey="dau" name="DAU" stroke={COLORS.secondary} strokeWidth={3} dot={{ r: 5, fill: COLORS.secondary }} activeDot={{ r: 7 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </ChartSection>

                    <ChartSection icon={<Percent style={{ fontSize: 22, color: COLORS.success }} />} title="User Retention Rate" description="Percentage of users who return and continue using the application after first use" timeframe={trackingContext}>
                        <Box sx={{ height: { xs: 280, sm: 350 }, width: '100%' }}>
                            <ResponsiveContainer>
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} unit="%" />
                                    <Tooltip formatter={(value) => `${value}%`} />
                                    <Legend />
                                    <Bar dataKey="retention_30" name="30-Day" fill={COLORS.success} radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="retention_90" name="90-Day" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="retention_120" name="120-Day" fill={COLORS.error} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </ChartSection>

                    <ChartSection icon={<TouchApp style={{ fontSize: 22, color: COLORS.secondary }} />} title="User Engagement Depth" description="Average sessions per user, time spent per session, and core features used" timeframe={trackingContext}>
                        <Box sx={{ height: { xs: 280, sm: 350 }, width: '100%' }}>
                            <ResponsiveContainer>
                                <ComposedChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} label={{ value: 'Sessions', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} label={{ value: 'Minutes', angle: 90, position: 'insideRight', style: { fontSize: 11 } }} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar yAxisId="left" dataKey="sessions" name="Avg Sessions/User" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                                    <Bar yAxisId="left" dataKey="features" name="Core Features Used" fill={COLORS.indigo} radius={[4, 4, 0, 0]} />
                                    <Line yAxisId="right" type="monotone" dataKey="duration" name="Avg Duration (min)" stroke={COLORS.warning} strokeWidth={3} dot={{ r: 5 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </Box>
                    </ChartSection>

                    <ChartSection icon={<TrendingUp style={{ fontSize: 22, color: COLORS.teal }} />} title="User Growth Rate (Month-on-Month)" description="How fast the user base is growing organically or through demand" timeframe={trackingContext}>
                        <Box sx={{ height: { xs: 260, sm: 300 }, width: '100%' }}>
                            <ResponsiveContainer>
                                <AreaChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                    <YAxis tick={{ fontSize: 12 }} unit="%" />
                                    <Tooltip formatter={(value) => `${value}%`} />
                                    <Area type="monotone" dataKey="growth" name="Growth Rate" stroke={COLORS.teal} fill={COLORS.lightGreen} strokeWidth={3} dot={{ r: 5, fill: COLORS.teal }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>
                    </ChartSection>

                    <ChartSection icon={<Visibility style={{ fontSize: 22, color: COLORS.indigo }} />} title="Clicks & Impressions" timeframe={trackingContext}>
                        <Box sx={{ height: { xs: 280, sm: 350 }, width: '100%' }}>
                            <ResponsiveContainer>
                                <ComposedChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} unit="%" />
                                    <Tooltip />
                                    <Legend />
                                    <Bar yAxisId="left" dataKey="impressions" name="Impressions" fill={COLORS.lightBlue} radius={[4, 4, 0, 0]} />
                                    <Bar yAxisId="left" dataKey="clicks" name="Clicks" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                                    <Line yAxisId="right" type="monotone" dataKey="ctr" name="CTR %" stroke={COLORS.error} strokeWidth={3} dot={{ r: 5 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </Box>
                    </ChartSection>

                    {/* Geographic Distribution */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                        <ChartSection icon={<Public style={{ fontSize: 22, color: COLORS.primary }} />} title="Geographic Distribution Over Time" timeframe={trackingContext}>
                            <Box sx={{ height: { xs: 260, sm: 300 }, width: '100%' }}>
                                <ResponsiveContainer>
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="uk" name="UK Users" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="nigeria" name="Nigeria Users" fill={COLORS.success} radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </ChartSection>

                        <ChartSection icon={<Public style={{ fontSize: 22, color: COLORS.success }} />} title="User Distribution (Latest)" timeframe={trackingContext}>
                            <Box sx={{ height: { xs: 240, sm: 280 }, width: '100%' }}>
                                {geoData.length > 0 && (geoData[0].value > 0 || geoData[1].value > 0) ? (
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie data={geoData} cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={5} dataKey="value"
                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                                                {geoData.map((entry, index) => (<Cell key={index} fill={GEO_COLORS[index]} />))}
                                            </Pie>
                                            <Tooltip formatter={(value) => value.toLocaleString()} />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280' }}>No geographic data available</div>
                                )}
                            </Box>
                            {latestMetric && (
                                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '16px' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.primary }}>{latestMetric.uk_users.toLocaleString()}</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>UK Users</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.success }}>{latestMetric.nigeria_users.toLocaleString()}</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>Nigeria Users</div>
                                    </div>
                                </div>
                            )}
                        </ChartSection>
                    </div>
                </section>
            )}

            {/* Empty state */}
            {!loading && !error && metrics.length === 0 && (
                <section style={{ maxWidth: '600px', margin: '0 auto', padding: '60px 16px', textAlign: 'center' }}>
                    <BarChartIcon style={{ fontSize: 64, color: '#d1d5db', marginBottom: '16px' }} />
                    <h3 style={{ color: '#111827', fontSize: '20px', marginBottom: '8px' }}>No Analytics Data Yet</h3>
                    <p style={{ color: '#6b7280' }}>Performance metrics have not been recorded yet. Check back later.</p>
                </section>
            )}

            {/* Footer */}
            <footer style={{ background: '#111827', color: 'white', padding: '48px 16px 24px 16px' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '32px', marginBottom: '32px' }}>
                        <div style={{ flex: '1 1 250px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                                <div style={{ background: '#2563eb', padding: '8px', borderRadius: '8px' }}>
                                    <Shield style={{ fontSize: 24, color: 'white' }} />
                                </div>
                                <span style={{ fontSize: '24px', fontWeight: 'bold' }}>RiskAS</span>
                            </div>
                            <p style={{ color: '#9ca3af', fontSize: '14px', lineHeight: 1.6 }}>
                                Advanced AI-powered risk assessment platform providing real-time analytics and insights.
                            </p>
                        </div>
                        <div style={{ flex: '0 1 auto' }}>
                            <h4 style={{ fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>Quick Links</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {[['/', 'Home'], ['/about', 'About'], ['/contact', 'Contact'], ['/analytics', 'Analytics']].map(([href, text]) => (
                                    <a key={href} href={href} style={{ color: '#9ca3af', textDecoration: 'none', fontSize: '14px' }}>{text}</a>
                                ))}
                            </div>
                        </div>
                        <div style={{ flex: '0 1 auto' }}>
                            <h4 style={{ fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>Legal</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {[['/terms', 'Terms of Service'], ['/privacy-policy', 'Privacy Policy'], ['/cookie-policy', 'Cookie Policy']].map(([href, text]) => (
                                    <a key={href} href={href} style={{ color: '#9ca3af', textDecoration: 'none', fontSize: '14px' }}>{text}</a>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid #374151', paddingTop: '16px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                        &copy; {new Date().getFullYear()} RiskAS. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AnalyticsDashboard;
